import moment from 'moment'
import type { Moment } from 'moment'
import { CountryModel } from '@/modules/shared/types/country'

export interface WorkTimeFractionModel {
  denominatorPart: number
  numeratorPart: number
}
export interface FieldsetSelectProps {
  label: string
  value: string
  data: keyof ContractDetailsModel
  required: boolean
  placeholder: string
  options: { label: string; value: string }[]
  children?: React.ReactNode
}

export interface ContractModel {
  id?: string
  parentContractId?: string
  startDate: Moment
  endDate: Moment
  isAnnex: boolean
  isMinimalContractRate: boolean
  workTime: WorkTimeFractionModel
  settlement: SettlementType
  isTaxExempt: boolean
  isFamilyFourPlusRelief?: boolean
  isSeniorRelief?: boolean
  includeTaxFreeAmount: boolean

  type: ContractType
  contractRate: number
  bonusRate?: number
  nightRate?: number
  lumpSumNightAmount?: number
  overtimeRate?: number
  dutyRate?: number
  euWorkRate?: number
  lumpSumAllRates?: number
  rateDE?: number
  rateFR?: number
  rateAT?: number
  includePit?: boolean
  isPpkEnabled?: boolean
  civilLawIncomeCosts?: number
  increasedTaxCosts?: TaxCostsType
  rateSummary?: number
}

export type ContractFormType =
  | 'contract-parameters'
  | 'wages'
  | 'taxes-and-deductions'
  | 'lumpscosts'
  | 'eu-qualifications'
  | 'additional-rates'
  | 'vacations'

export type ContractFromContextType = {
  countries: CountryModel[]
  countriesOptions: any
  isEditState: boolean
  isReadyToSubmit: boolean
  setIsReadyToSubmit: React.Dispatch<React.SetStateAction<boolean>>
  setFormTab: React.Dispatch<React.SetStateAction<ContractFormType>>
  formTab: ContractFormType
  allTabs: { type: ContractFormType; label: string }[]

  data: ContractDetailsModel | null
  sourceData: ContractDetailsModel | null
  setData: React.Dispatch<React.SetStateAction<ContractDetailsModel | null>>
  isAnnexState: boolean
  lastAnnexDateState: string
  companyId: string
  driverId?: number
}
export interface ContractDetailsModel {
  parentContractId?: string
  id?: string
  startDate: string
  endDate: string
  isAnnex: boolean
  workTime: WorkTimeFractionModel
  settlement: SettlementType
  type: ContractType
  calculateDutyInCivilLawAgreement?: boolean
  personalScheduleMode?: boolean
  mapActivityForCivilAgreement?: boolean
  alimonyDeduction?: boolean
  isMinimalContractRate?: boolean
  contractRate: number
  weekWorkTimeHours: number

  driverQualificationsAustria: DriverQualificationsType
  driverQualificationsBelgium: DriverQualificationsType
  driverQualificationsFinland: DriverQualificationsType
  driverQualificationsFrance: DriverQualificationsType
  driverQualificationsItaly: DriverQualificationsType
  driverQualificationsLuxembourg: DriverQualificationsType
  driverQualificationsCzech: DriverQualificationsType
  driverQualificationsDenmark: DriverQualificationsType

  skipForeignMinimalAmountCalculation: boolean
  skipAccommodationForMinimalAmount: boolean
  skipAllowanceForMinimalAmount: boolean

  initialLeaveConfigurationDay: string
  workYears: number
  daysOfOverdueLeave: number
  daysOfUsedLeave: number
  daysOfLeave: number
  daysOfLeaveAtTheRequest: number

  euWorkRate?: number

  lumpSumAllRates?: number

  lumpSumOvertimeRate?: number
  reduceOvertimeRates?: boolean
  reduceOvertimeRatesForSickLeave?: boolean
  reduceOvertimeRatesForLeave?: boolean
  reduceOvertimeRatesForNotPaidLeave?: boolean

  lumpSumDutyRate?: number
  reduceDutyRates?: boolean
  reduceDutyRatesForSickLeave?: boolean
  reduceDutyRatesForLeave?: boolean
  reduceDutyRatesForNotPaidLeave?: boolean

  lumpSumNightRate?: number
  reduceNightRates?: boolean
  reduceNightRatesForSickLeave?: boolean
  reduceNightRatesForLeave?: boolean
  reduceNightRatesForNotPaidLeave?: boolean

  lumpSumNightHourRate?: any

  lumpSumOvertimeFiftyPercentRate?: any
  lumpSumOvertimeOneHundredPercentRate?: any
  lumpSumDutyFiftyPercentRate?: any
  lumpSumDutyOneHundredRate?: any

  reduceRateOfAbsenceForSickLeave?: boolean
  reduceRateOfAbsenceForLeave?: boolean
  reduceRateOfAbsenceForNotPaidLeave?: boolean

  // Pomniejszanie stawki zasadniczej o nieobecności (parytet z WPF: checkbox przy
  // "Stawka zasadnicza" + sekcja "*Pomniejszaj:"). Osobne od reduceRateOfAbsenceFor*,
  // które dotyczą ryczałtów.
  reduceContractDataForSickLeave?: boolean
  reduceContractDataForLeave?: boolean
  reduceContractDataForNotPaidLeave?: boolean

  reduceAdditionalRatesForLeave?: boolean
  reduceAdditionalRatesForNotPaidLeave?: boolean
  reduceAdditionalRatesForSickLeave?: boolean

  reduceBonusRates?: boolean
  reduceBonusRatesForSickLeave?: boolean
  reduceBonusRatesForLeave?: boolean
  reduceBonusRatesForNotPaidLeave?: boolean

  reduceAllRates?: boolean
  reduceEuWorkRates?: boolean
  reduceEuWorkRatesForSickLeave?: boolean
  reduceEuWorkRatesForLeave?: boolean
  reduceEuWorkRatesForNotPaidLeave?: boolean

  reduceRewardRates?: boolean
  reduceRewardRatesForSickLeave?: boolean
  reduceRewardRatesForLeave?: boolean
  reduceRewardRatesForNotPaidLeave?: boolean
  reduceMonthRate?: boolean
  reduceNightWork?: boolean
  reduceOverhoures?: boolean
  reduceDuty?: boolean
  reduceAdditionForEuWork?: boolean

  duty50Rate?: number
  duty100Rate?: number
  nightHoursRate?: number
  bonusRate?: number
  rewardRate?: number

  overtimeAlwaysAs100?: boolean

  duty50RateOption: RatesOption
  duty100RateOption: RatesOption
  nightHoursRateOption: RatesOption
  bonusRateOption: RatesOption
  rewardRateOption: RatesOption

  includeTaxFreeAmount?: boolean
  taxFreeAmount?: TaxFreeAmount
  isNewOrderEnabled?: boolean
  isTaxExempt?: boolean
  isFamilyFourPlusRelief?: boolean
  isSeniorRelief?: boolean

  isTaxOldVersionDisabled?: boolean
  isSecondThresholdTaxEnabled?: boolean

  taxBase?: number

  increasedTaxCosts?: TaxCostsType
  increasedCivilLawCostsOfIncome?: CivilLawCostsOfIncomeType
  nettoType?: NettoRateType

  contractNettoRate?: number
  additionalNettoRate?: number

  isPpkEnabled?: boolean
  ppkEmployee?: number
  ppkEmployer?: number

  includeSickInsurance?: boolean
  includeSocialInsurance?: boolean
  bridgePensionFund?: boolean
  homelandTaxResidence?: boolean
  includePit?: boolean

  enabledNewOrderSecondEdition?: boolean

  isDeductionForMedicalInsuranceEnabled?: boolean
  deductionForMedicalInsurance?: number

  isAdditionForLaundryEnabled?: boolean
  additionForLaundry?: number
  additionForLaundryType?: AdditionForLaundryType
  reduceAdditionForLaundry?: boolean
  reduceAdditionForLaundryForSickLeave?: boolean
  reduceAdditionForLaundryForLeave?: boolean
  reduceAdditionForLaundryForNotPaidLeave?: boolean

  isLifeInsuranceEnabled?: boolean
  lifeInsuranceValue?: number
  lifeInsuranceValueWorker?: number

  isAdditionForBhpEnabled?: boolean
  additionForBhp?: number
  additionForBhpType?: number
  reduceAdditionForBhp?: boolean
  reduceAdditionForBhpForSickLeave?: boolean
  reduceAdditionForBhpForLeave?: boolean
  reduceAdditionForBhpForNotPaidLeave?: boolean
  reduceCustomDeductionForSickLeave?: boolean
  reduceCustomDeductionForLeave?: boolean
  reduceCustomDeductionForNotPaidLeave?: boolean
  customAdditions?: CustomAddition[]
  reduceCustomDeduction?: boolean
  additionType: CustomAdditionType
  reduceCustomAddition?: boolean
  reduceCustomAdditionForSickLeave?: boolean
  reduceCustomAdditionForLeave?: boolean
  reduceCustomAdditionForNotPaidLeave?: boolean
  ReduceCustomAddition?: CustomDeduction[]
  customDeductions: any
}

export interface CustomAddition {
  id?: string
  name: string
  value: number
  additionType: CustomAdditionType
  reduceCustomAddition?: boolean
  reduceCustomAdditionForSickLeave?: boolean
  reduceCustomAdditionForLeave?: boolean
  reduceCustomAdditionForNotPaidLeave?: boolean
}

export interface CustomDeduction {
  id?: string
  name: string
  employerContribution: number
  employeeContribution: number
  deductionType: CustomDeductionType
  reduceCustomDeduction?: boolean
  reduceCustomDeductionForSickLeave?: boolean
  reduceCustomDeductionForLeave?: boolean
  reduceCustomDeductionForNotPaidLeave?: boolean
}

export enum CustomAdditionType {
  Day = 0,
  Month = 1,
}

export enum CustomDeductionType {
  Day = 0,
  Month = 1,
}

export enum AdditionForLaundryType {
  Day = 0,
  Month = 1,
}

export enum TaxCostsType {
  Normal = 0,
  Increased = 1,
  None = 2,
}

export enum NettoRateType {
  MonthRate = 0,
  DayRate = 1,
  DayRateSepareted = 2,
  KilometerRate = 3,
}

export enum TaxFreeAmount {
  Full = 0,
  Half = 1,
  OneThird = 2,
  None = 3,
}

export enum CivilLawCostsOfIncomeType {
  Normal = 0,
  Increased = 1,
  None = 2,
}

export enum RatesOption {
  ConstantValue = 0,
  PercentFromAmount = 1,
  PercentFromMinimalRate = 2,
  FiftyPercentFromAmount = 3,
  OneHundredPercentFromAmount = 4,
  NotSet = 5,
  PercentFromAmountAndLumpSum = 6,
}

export enum DriverQualificationsType {
  NotSet = 'NotSet',
  VehiclesTo3Comma5 = 'VehiclesTo3Comma5',
  VehiclesFrom3Comma5To11 = 'VehiclesFrom3Comma5To11',
  VehiclesFrom11To19 = 'VehiclesFrom11To19',
  VehiclesAbove19 = 'VehiclesAbove19',
  HighQualifiedTruckDriver = 'HighQualifiedTruckDriver',
  BusDriver = 'BusDriver',
  BusDriverAndConductor = 'BusDriverAndConductor',
  TouristBusDriver = 'TouristBusDriver',
  BigBusDriver = 'BigBusDriver',
  VehiclesTo3Comma5Standard = 'VehiclesTo3Comma5Standard',
  VehiclesAbove3Comma5Below3Axis = 'VehiclesAbove3Comma5Below3Axis',
  VehiclesAbove3Comma5Abov3Axis = 'VehiclesAbove3Comma5Abov3Axis',
  VehiclesBelow8Group3 = 'VehiclesBelow8Group3',
  VehiclesAbove8Group3Super = 'VehiclesAbove8Group3Super',
  UnqualifiedDriver = 'UnqualifiedDriver',
  QualifiedDriver = 'QualifiedDriver',
  Guard = 'Guard',
  DriverDuringTraining = 'DriverDuringTraining',
  VhiclesBelow7 = 'VhiclesBelow7',
  JouniorCourier = 'JouniorCourier',
  VehiclesAbove7Below15 = 'VehiclesAbove7Below15',
  SeniorCourier = 'SeniorCourier',
  VehiclesAbove15 = 'VehiclesAbove15',
  BelgiumFirstGroup = 'BelgiumFirstGroup',
  BelgiumSecondGroup = 'BelgiumSecondGroup',
  BelgiumThirdGroup = 'BelgiumThirdGroup',
  BelgiumFourthGroup = 'BelgiumFourthGroup',
  VehicleCombination = 'VehicleCombination',
  DangerousGoods = 'DangerousGoods',
  EmployeesWithSpecialistQualifications = 'EmployeesWithSpecialistQualifications',
  ProfessionalDriverVehiclesAbove3Comma5Below3Axis = 'ProfessionalDriverVehiclesAbove3Comma5Below3Axis',
  ProfessionalDriverVehiclesAbove3Comma5Abov3Axis = 'ProfessionalDriverVehiclesAbove3Comma5Abov3Axis',
  ProfessionalDriverVehicleCombination = 'ProfessionalDriverVehicleCombination',
  TankDriver = 'TankDriver',
  TransportOfPeopleDriver = 'TransportOfPeopleDriver',
  TransportOfPeopleProfessionalDriver = 'TransportOfPeopleProfessionalDriver',
  TransportOfPeopleQualifiedWorker = 'TransportOfPeopleQualifiedWorker',
  TransportOfPeopleSemiSkilledWorker = 'TransportOfPeopleSemiSkilledWorker',
  TransportOfPeopleForeman = 'TransportOfPeopleForeman',
  TransportOfPeopleGarageWorker = 'TransportOfPeopleGarageWorker',
  FinlandBusDriver = 'FinlandBusDriver',
  TrackDriverWithTrailer = 'TrackDriverWithTrailer',
  TractorDriver = 'TractorDriver',
  RegularDriver = 'RegularDriver',
  VehiclesAbove3Comma5Driver = 'VehiclesAbove3Comma5Driver',
  CarriageOfGoods = 'CarriageOfGoods',
  CabotageInPassengerTransport = 'CabotageInPassengerTransport',
  TouristTransport = 'TouristTransport',
  CcnlC3 = 'CcnlC3',
  CcnlB3 = 'CcnlB3',
  CcnlA3 = 'CcnlA3',
  CcnlF2 = 'CcnlF2',
  CcnlE2 = 'CcnlE2',
  CcnlD2 = 'CcnlD2',
  CcnlH1 = 'CcnlH1',
  CcnlG1 = 'CcnlG1',
  CcnlI110 = 'CcnlI110',
  CcnlI116 = 'CcnlI116',
  CcnlL110 = 'CcnlL110',
  CcnlL116 = 'CcnlL116',
  CcnlL119 = 'CcnlL119',
}

export enum SettlementType {
  NotSet = 'NotSet',
  Month = 'Month',
  Hour = 'Hour',
  ForContract = 'ForContract',
}

export enum SettlementTypeByNumber {
  NotSet = 1,
  Month = 2,
  Hour = 3,
}

export enum ContractType {
  NotSet = 'NotSet',
  Equivalent = 'Equivalent',
  Basic = 'Basic',
  Task = 'Task',
  Mix = 'Mix',
  Intermittent = 'Intermittent',
  CivilLawAgreement = 'CivilLawAgreement',
  SelfEmployment = 'SelfEmployment',
}
